import {
  Anchor,
  Box,
  Button,
  Center,
  Group,
  LoadingOverlay,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { DatePicker, MonthPicker, YearPicker } from "@mantine/dates";
import { IconDownload, IconFileSpreadsheet } from "@tabler/icons-react";
import { exportApi } from "apis/exportApi";
import { CURRENT_YEAR, PERIOD_OPTIONS } from "constants/common/date";
import {
  ExportFilters,
  ExportMode,
  ExportPeriod,
  ExportRequestAsyncResponse,
} from "constants/types/export";
import { PermissionRequirement } from "constants/types/permission";
import { FC, FormEvent, useState } from "react";
import { useNotification } from "utils/hooks";
import { useAbility } from "configs/casl";
import { canAccessRequirement } from "utils/helpers/authorization";
import { useAppModal } from "utils/hooks/ui/useAppModal";
import CustomText from "../CustomText/CustomText";
import { ExportModule } from "constants/common/exportJob";
import dayjs from "configs/dayjs";
import "dayjs/locale/vi";

const FORM_ID = "export-excel-form";
const EXPORT_MODE_OPTIONS = [
  ...PERIOD_OPTIONS,
  { value: "custom", label: "Tuỳ chọn" },
];

interface ExportExcelContentProps {
  module: ExportModule;
  /** Bộ lọc đang đặt ngoài màn danh sách; chỉ dùng ở chế độ "Tuỳ chọn" */
  filters?: ExportFilters;
}

type DateRangeValue = [Date | null, Date | null];
type QuarterValue = {
  year: Date | null;
  quarter: number | null;
};

/** Bỏ các khoá rỗng để không gửi tham số thừa lên API */
const cleanFilters = (f?: ExportFilters): Record<string, unknown> => {
  if (!f) return {};
  return Object.fromEntries(
    Object.entries(f).filter(([, v]) =>
      Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && v !== "",
    ),
  );
};

const getQuarter = (date: Date) => Math.floor(date.getMonth() / 3) + 1;

interface QuarterPickerProps {
  value: QuarterValue;
  onChange: (value: QuarterValue) => void;
}

const QuarterPicker: FC<QuarterPickerProps> = ({ value, onChange }) => {
  const handleYearChange = (year: Date | null) => {
    onChange({ ...value, year });
  };

  const handleQuarterSelect = (quarter: number) => {
    onChange({ ...value, quarter });
  };

  return (
    <Stack gap="xs">
      <Center>
        <YearPicker
          value={value.year}
          onChange={handleYearChange}
          locale="vi"
          size="sm"
        />
      </Center>
      <SimpleGrid cols={2} spacing="xs">
        {[1, 2, 3, 4].map((quarter) => (
          <Button
            key={quarter}
            size="sm"
            variant={value.quarter === quarter ? "filled" : "light"}
            onClick={() => handleQuarterSelect(quarter)}
            disabled={!value.year}
          >
            Quý {quarter}
          </Button>
        ))}
      </SimpleGrid>
      {value.quarter && value.year && (
        <Text size="sm" c="dimmed" ta="center">
          Đang chọn: Quý {value.quarter} / {value.year.getFullYear()}
        </Text>
      )}
    </Stack>
  );
};

const getFilenameFromDisposition = (disposition?: string) => {
  if (!disposition) return null;
  const encodedMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) return decodeURIComponent(encodedMatch[1]);

  const match = disposition.match(/filename="?([^"]+)"?/i);
  return match?.[1] ?? null;
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const downloadFromUrl = (url: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.click();
};

const ExportExcelContent: FC<ExportExcelContentProps> = ({
  module,
  filters,
}) => {
  const { notification } = useNotification();
  const currentDate = new Date();
  const [mode, setMode] = useState<ExportMode>("month");
  const [monthDate, setMonthDate] = useState<Date | null>(currentDate);
  const [quarterValue, setQuarterValue] = useState<QuarterValue>({
    year: currentDate,
    quarter: getQuarter(currentDate),
  });
  const [yearDate, setYearDate] = useState<Date | null>(
    new Date(CURRENT_YEAR, 0, 1),
  );
  const [range, setRange] = useState<DateRangeValue>([null, null]);
  const [isLoading, setIsLoading] = useState(false);
  const [resultUrl, setResultUrl] = useState<string | null>(null);

  const handleModeChange = (v: string) => {
    if (!v) return;
    setMode(v as ExportMode);
    setResultUrl(null);
  };

  const getPreGeneratedParams = () => {
    if (mode === "quarter") {
      if (!quarterValue.year || !quarterValue.quarter) return null;

      return {
        module,
        period: mode,
        year: quarterValue.year.getFullYear(),
        value: quarterValue.quarter,
      };
    }

    const selectedDate = mode === "month" ? monthDate : yearDate;
    if (!selectedDate) return null;

    const period = mode as ExportPeriod;
    return {
      module,
      period,
      year: selectedDate.getFullYear(),
      ...(period === "month" ? { value: selectedDate.getMonth() + 1 } : {}),
    };
  };

  const handleCustomExport = async () => {
    const [start, end] = range;
    if (!start || !end) {
      notification.error({ title: "Vui lòng chọn khoảng ngày" });
      return;
    }

    const fromDate = dayjs(start).startOf("day").unix();
    const toDate = dayjs(end).endOf("day").unix();
    // Chỉ chế độ "Tuỳ chọn" mới áp bộ lọc của màn danh sách. Tháng/quý/năm
    // đọc file dựng sẵn lúc 23:59 nên không thể lọc.
    const res = await exportApi.requestExport({
      module,
      fromDate,
      toDate,
      ...cleanFilters(filters),
    });
    const contentType = String(res.headers["content-type"] ?? "");
    const blob = res.data;

    if (contentType.includes("application/json")) {
      const json = JSON.parse(await blob.text()) as {
        success: boolean;
        data?: ExportRequestAsyncResponse;
      };
      if (json.success && json.data?.async) {
        notification.success({
          title: "Đã tạo yêu cầu xuất Excel",
          message: `Job ${json.data.jobId} đang chờ xử lý (${json.data.totalRows} dòng).`,
          autoClose: 5000,
        });
        return;
      }

      notification.error({ title: "Tạo yêu cầu xuất Excel thất bại" });
      return;
    }

    const contentDisposition = String(res.headers["content-disposition"] ?? "");
    const filename =
      getFilenameFromDisposition(contentDisposition) ??
      `${module}-${fromDate}-${toDate}.xlsx`;
    downloadBlob(blob, filename);
    notification.success({ title: "Xuất Excel thành công" });
  };

  const handlePreGeneratedExport = async () => {
    const params = getPreGeneratedParams();
    if (!params) {
      notification.error({ title: "Vui lòng chọn thời gian xuất Excel" });
      return;
    }

    const res = await exportApi.getPreGenerated(params);
    if (res.data.success) {
      const url = res.data.data?.url;
      if (!url) {
        notification.error({ title: "Không tìm thấy đường dẫn tải file" });
      } else {
        setResultUrl(url);
        downloadFromUrl(url);
        notification.success({ title: "Xuất Excel thành công" });
      }
    } else {
      notification.error({ title: "Tạo file Excel thất bại" });
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLoading) return;

    setIsLoading(true);
    setResultUrl(null);
    try {
      if (mode === "custom") await handleCustomExport();
      else await handlePreGeneratedExport();
    } catch {
      notification.error({ title: "Tạo file Excel thất bại" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box pos="relative">
      <LoadingOverlay visible={isLoading} />
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <Stack gap="sm">
          <SegmentedControl
            data={EXPORT_MODE_OPTIONS}
            value={mode}
            onChange={handleModeChange}
            fullWidth
          />
          {mode === "month" && (
            <Stack gap={4}>
              <Center>
                <MonthPicker
                  value={monthDate}
                  locale="vi"
                  onChange={(v) => {
                    setMonthDate(v);
                    setResultUrl(null);
                  }}
                />
              </Center>
            </Stack>
          )}
          {mode === "quarter" && (
            <Stack gap={4}>
              <QuarterPicker
                value={quarterValue}
                onChange={(nextValue) => {
                  setQuarterValue(nextValue);
                  setResultUrl(null);
                }}
              />
            </Stack>
          )}
          {mode === "annual" && (
            <Stack gap={4}>
              <Center>
                <YearPicker
                  value={yearDate}
                  locale="vi"
                  onChange={(v) => {
                    setYearDate(v);
                    setResultUrl(null);
                  }}
                />
              </Center>
            </Stack>
          )}
          {mode === "custom" && (
            <Stack gap={4}>
              <Center>
                <DatePicker
                  type="range"
                  // Cho phép chọn trọn một ngày (đầu = cuối), xem DateRangeInput
                  allowSingleDateInRange
                  value={range}
                  locale="vi"
                  onChange={(v) => {
                    setRange(v as DateRangeValue);
                    setResultUrl(null);
                  }}
                />
              </Center>
            </Stack>
          )}
          <Button
            type="submit"
            leftSection={<IconDownload size={16} />}
            loading={isLoading}
            color="#217346"
            fullWidth
          >
            Tải xuống
          </Button>
          {resultUrl && (
            <Group gap="xs" align="center">
              <Text size="sm" c="dimmed" style={{ flex: 1 }} truncate>
                {resultUrl}
              </Text>
              <Anchor
                href={resultUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="never"
              >
                <Button
                  size="xs"
                  variant="light"
                  color="green"
                  leftSection={<IconDownload size={14} />}
                >
                  Tải xuống
                </Button>
              </Anchor>
            </Group>
          )}
        </Stack>
      </form>
    </Box>
  );
};

interface ExportExcelActionProps {
  module: ExportModule;
  buttonLabel?: string;
  permission?: PermissionRequirement;
  /** Bộ lọc đang đặt ngoài màn danh sách, áp cho chế độ "Tuỳ chọn" */
  filters?: ExportFilters;
}

const ExportExcelAction: FC<ExportExcelActionProps> = ({
  module,
  buttonLabel = "Xuất Excel",
  permission,
  filters,
}) => {
  const ability = useAbility();
  const { openCrudModal } = useAppModal();

  if (!canAccessRequirement(ability, permission, true)) return null;

  const handleOpen = () => {
    openCrudModal(
      <CustomText variant="section-title">Xuất file Excel</CustomText>,
      {
        mode: "view",
        hideFooter: true,
        children: <ExportExcelContent module={module} filters={filters} />,
      },
      "sm",
    );
  };

  return (
    <Button
      leftSection={<IconFileSpreadsheet size={18} />}
      variant="filled"
      onClick={handleOpen}
      color="#217346"
    >
      {buttonLabel}
    </Button>
  );
};

export default ExportExcelAction;
